import { captureException } from '@sentry/react';
import { getStripeAccountIdByState } from 'domains/billing';
import { EvaluationDuration } from 'domains/calendars/types';
import { ContactRelationship } from 'domains/client';
import {
  scheduleEvaluationFromSignupV3,
  ScheduleSignupEvaluationResponse,
  ScheduleSignupEvaluationResponseError,
  SignupErrorCode,
  SignupSchedule,
} from 'domains/evaluations/evaluationSchedule';
import { Physician, PhysicianForm, SalesforcePayerTypes } from 'domains/signup/data/types';
import { SUBSCRIBER_RELATIONSHIP_CODE_MAP } from 'domains/signupDirect/businessLogic/relationships';
import { DirectSignupFilters, ResumedLeadState } from 'domains/signupDirect/context';
import { getInsurancePrimarySubscriberDetails } from 'domains/signupDirect/Evaluation/Insurance/eligibility';
import {
  getFilteringAreasByConcernKeys,
  mapClinicalConcernsToClinicalAreas,
  mergeCommunicationConcerns,
} from 'domains/signupDirect/misc/communicationConcerns';
import {
  getEvaluationPrice,
  OUT_OF_NETWORK_EVALUATION_PRICE,
  PRIVATE_PAY_EVALUATION_PRICE,
} from 'domains/signupDirect/misc/evalPrice';
import { clearTempLeadId, getTempLeadId, setTempLeadId } from 'domains/signupDirect/misc/leadStorage';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import { EvaluationFormType, getPayerTypeFromBillingInfo } from 'domains/signupDirect/schemas/evaluationSchema';
import { createSalesforceLead, updateSalesforceLead } from 'domains/signupv4/Leads/queries';
import { LeadDesiredPaymentMethod, SignupLeadV4 } from 'domains/signupv4/Leads/types';
import { getSFInsuranceType } from 'domains/signupv4/Parent/actors';
import { getGCLIDParameters } from 'domains/tracking/GCLIDtracking';
import { getUTMParameters } from 'domains/tracking/UTMTracking';
import { LDClient, LDFlagSet } from 'launchdarkly-react-client-sdk';
import { get, isUndefined } from 'lodash';
import mixpanel from 'mixpanel-browser';
import { extractDate } from 'utils/date';
import { removeWhitespaces } from 'utils/phoneNumber';
import { SignupError } from '../../../utils/errors/debuggableError';
import { SIGNUP_LAUNCHDARKLY_EVENTS } from '../misc/launchDarklyTracking';
import { onClearSignupLocalStorage } from '../misc/localStorage';
import { EvaluationScheduleFormType, isSplitEvaluationSchema } from '../schemas/evaluationScheduleSchema';
import { DEFAULT_IN_NETWORK_ESTIMATES } from '../SyntaxComps/constants';

type SubmitParams = {
  data: EvaluationFormType;
  filters: DirectSignupFilters;
  resumedLeadState?: ResumedLeadState;
  LDClient?: LDClient;
  flags?: LDFlagSet;
  hasSwitchedToPrivatePay?: boolean;
};

type Result =
  | {
      success: true;
      scheduleResponse: ScheduleSignupEvaluationResponse;
    }
  | {
      success: false;
      error: SignupErrorCode;
      message?: string;
    };

const getLDTrackExtras = (params: SubmitParams) => {
  const { data, resumedLeadState } = params;

  return {
    amountOfSessions:
      data.evaluationSchedule.type === 'single' ? 1 : isUndefined(data.evaluationSchedule.secondTimeSlot) ? 1 : 2,
    isInsurance:
      data.billingInfo.type === 'insurance-commercial' ||
      data.billingInfo.type === 'insurance-medicaid' ||
      data.billingInfo.type === 'insurance-medicare',
    isPrivatePay: data.billingInfo.type === 'privatePay',
    isResumedLead: !!resumedLeadState,
    evalType: data.evaluationSchedule.type,
    isExistingContact: data.loginContactInfo.isAlreadyContact,
  };
};

export const submitEvalForm = async (params: SubmitParams): Promise<Result> => {
  const { LDClient, flags } = params;

  LDClient?.track(
    SIGNUP_LAUNCHDARKLY_EVENTS.LANDING_PAGE_EXPERIMENT.SUBMITTED_EVALUATION.ATTEMPT,
    getLDTrackExtras(params),
  );

  if (params.data.billingInfo.type === 'privatePay') {
    return submitPrivatePay(params);
  }

  if (
    params.data.billingInfo.type === 'insurance-commercial' ||
    params.data.billingInfo.type === 'insurance-medicaid' ||
    params.data.billingInfo.type === 'insurance-medicare'
  ) {
    return submitInsurance({ ...params, flags });
  }

  throw new SignupError('Expected billingInfo.type to be either privatePay or insurance');
};

const submitPrivatePay = async (params: SubmitParams): Promise<Result> => {
  const { data, filters, resumedLeadState, LDClient, hasSwitchedToPrivatePay } = params;
  if (data.billingInfo.type !== 'privatePay') throw new SignupError("Expected billingInfo.type to be 'privatePay'");

  const isResumedLead = !isUndefined(resumedLeadState?.leadID);

  const UTMParams = getUTMParameters();
  const GCLIDParams = getGCLIDParameters();

  const { clientInfo, contactInfo, evaluationSchedule } = data;
  // Try to get leadId from form data first, then from localStorage as fallback
  let leadId = data.leadId ?? getTempLeadId();

  const leadObject = {
    communicationConcerns: data.concerns.clinicalConcernKeys.join(','),
    detectedAreas: data.concerns.clinicalAreas,
    desiredPaymentMethod:
      filters.paymentMethod.type === 'privatePay' ? 'self-pay' : ('Insurance' as LeadDesiredPaymentMethod),
    selfSignUp: true,
    address: {
      state: data.clientInfo.stateCode,
    },
    firstName: data.clientInfo.firstName,
    lastName: data.clientInfo.lastName,
    contactFirstName: data.contactInfo.firstName,
    contactLastName: data.contactInfo.lastName,
    email: data.loginContactInfo.email,
    dob: data.clientInfo.dateOfBirth,
    phone: data.contactInfo.phone,
    serviceFor: data.clientType,
    primaryLanguage: filters.language,
    relationshipToClient: data.contactInfo.relationshipToClient,
    insuranceType: SalesforcePayerTypes.SELF_PAY,
    signupFlowOutcome: 'Booked for Evaluation',
    sourceSystem: 'Signup Direct',
    contactTimezone: evaluationSchedule.timeZone,
    ...(data.evaluationSchedule.type === 'single'
      ? {
          evaluationDate: data.evaluationSchedule.timeSlot.time,
        }
      : {
          evaluationDate: data.evaluationSchedule.firstTimeSlot.time,
        }),
    ...UTMParams,
    ...GCLIDParams,
  } satisfies Partial<SignupLeadV4>;

  // Check if leadId already exists, update lead if it does
  // we don't need a new lead if we're resuming
  if (!leadId) {
    const leadResponse = await createSalesforceLead({
      ...leadObject,
      leadSource: 'Self-Referral - Web Inquiry',
    });
    leadId = leadResponse.id;
    // Store leadId in localStorage for persistence across form submissions
    setTempLeadId(leadId);
  } else {
    await updateSalesforceLead({
      ...leadObject,
      id: leadId,
    });
  }

  // Get stripe data
  const stripeData = await getStripeAccountIdByState(filters.stateCode);

  const {
    contactInfo: { relationshipToClient },
  } = data;

  const schedules = getSchedulesPayload(evaluationSchedule);

  // Submit final form with schedule
  let scheduleResponse;
  try {
    scheduleResponse = await scheduleEvaluationFromSignupV3({
      isResumeFlow: isResumedLead,
      address: { state: filters.stateCode },
      amount: PRIVATE_PAY_EVALUATION_PRICE, // @TODO does BE even need this?
      clientFirstName: clientInfo.firstName,
      clientLastName: clientInfo.lastName,

      ...(data.loginContactInfo.isAlreadyContact
        ? {
            contactID: data.loginContactInfo.contactId,
          }
        : { password: data.loginContactInfo.newPassword }),

      contactFirstName:
        data.contactInfo.relationshipToClient === ContactRelationship.Client
          ? data.clientInfo.firstName
          : (data.contactInfo.firstName as string),
      contactLastName:
        data.contactInfo.relationshipToClient === ContactRelationship.Client
          ? data.clientInfo.lastName
          : (data.contactInfo.lastName as string),
      contactTimeZone: evaluationSchedule.timeZone,
      schedules,
      schedulesTimezone: evaluationSchedule.timeZone,
      dob: extractDate(clientInfo.dateOfBirth),
      email: data.loginContactInfo.email,
      isClientMinor: filters.therapyTarget === 'child',
      phone: { mobile: removeWhitespaces(contactInfo.phone) },
      relationshipToClient: relationshipToClient,
      skipStripePayment: false,
      isCommercialInsurance: false,
      isMedicaidInsurance: false,
      leadID: leadId as string,
      stripeAccountID: stripeData.accountId,
      stripePaymentMethodID: data.billingInfo.stripeMethodId,

      prefilledAppointmentID: resumedLeadState?.prefilledAppointment?.appointmentID,

      clinicalAreas: data.concerns.clinicalAreas,
      clinicalConcerns: data.concerns.clinicalConcernKeys, // just as they are listed in the form
      filteringClinicalAreas: getFilteringAreasByConcernKeys(data.concerns.clinicalConcernKeys, filters.therapyTarget),
      mappedClinicalConcerns: mapClinicalConcernsToClinicalAreas(
        data.concerns.clinicalConcernKeys,
        filters.therapyTarget,
      ),
    });

    if (scheduleResponse.status === 'signup failed') {
      throw scheduleResponse;
    }
  } catch (e) {
    handleServerSideErrors({ data, filters, resumedLeadState, LDClient }, e);

    return {
      success: false,
      error: (e as ScheduleSignupEvaluationResponseError).error.code,
      message: (e as ScheduleSignupEvaluationResponseError).error.message,
    };
  }

  await trackSuccessfulEval(isResumedLead, params, LDClient);

  if (hasSwitchedToPrivatePay) {
    mixpanel.track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.REDIRECTED_TO_SELF_PAY.PAYMENT_COMPLETED);
  }

  // Clear temporary leadId from localStorage on successful submission
  clearTempLeadId();

  onClearSignupLocalStorage();

  return {
    success: true,
    scheduleResponse,
  };
};

const submitInsurance = async (params: SubmitParams): Promise<Result> => {
  const { data, filters, resumedLeadState, LDClient, flags } = params;
  if (
    data.billingInfo.type !== 'insurance-commercial' &&
    data.billingInfo.type !== 'insurance-medicaid' &&
    data.billingInfo.type !== 'insurance-medicare'
  ) {
    throw new SignupError("Expected billingInfo.type to be 'insurance'");
  }

  const isResumedLead = !isUndefined(resumedLeadState?.leadID);

  const UTMParams = getUTMParameters();
  const GCLIDParams = getGCLIDParameters();

  const { clientInfo, contactInfo, evaluationSchedule } = data;
  // Try to get leadId from form data first, then from localStorage as fallback
  let leadId = data.leadId ?? getTempLeadId();
  let primaryPhysician: PhysicianForm | undefined;

  if (
    data.billingInfo.type === 'insurance-medicaid' ||
    data.billingInfo.type === 'insurance-medicare' ||
    data.billingInfo.type === 'insurance-commercial'
  ) {
    primaryPhysician = {
      name: data.primaryPhysician.name ?? '',
      office: data.primaryPhysician.officeName ?? '',
      phoneNumber: data.primaryPhysician.phone ?? '',
      website: data.primaryPhysician.website ?? '',
      npi: data.primaryPhysician.npi ?? '',
    } as PhysicianForm;
  }

  const subscriberDetails = getInsurancePrimarySubscriberDetails(data);
  const leadObject = {
    address: {
      state: filters.stateCode,
    },
    email: data.loginContactInfo.email,
    firstName: clientInfo.firstName,
    lastName: clientInfo.lastName,
    contactFirstName: data.contactInfo.firstName,
    contactLastName: data.contactInfo.lastName,
    phone: contactInfo.phone,
    ...UTMParams,
    ...GCLIDParams,
    communicationConcerns: mergeCommunicationConcerns(data.concerns.clinicalConcernKeys, data.concerns.clinicalAreas),
    detectedAreas: data.concerns.clinicalAreas,
    primaryPhysician,
    desiredPaymentMethod: 'Insurance' as LeadDesiredPaymentMethod,
    selfSignUp: true,
    dob: data.clientInfo.dateOfBirth,
    serviceFor: data.clientType,
    primaryLanguage: filters.language,
    relationshipToClient: data.contactInfo.relationshipToClient,
    ...(evaluationSchedule.type === 'single'
      ? {
          evaluationDate: evaluationSchedule.timeSlot.time,
        }
      : {
          evaluationDate: evaluationSchedule.firstTimeSlot.time,
        }),
    memberID: data.billingInfo.memberId ?? '',
    insuranceType: getSFInsuranceType(data.billingInfo.insuranceData.insuranceType, false),
    subscriber: {
      dateOfBirth: subscriberDetails.dateOfBirth,
      firstName: subscriberDetails.firstName,
      lastName: subscriberDetails.lastName,
      relationshipToClient: SUBSCRIBER_RELATIONSHIP_CODE_MAP[subscriberDetails.relationShipToClient],
    },
    signupFlowOutcome: 'Booked for Evaluation',
    sourceSystem: 'Signup Direct',
    contactTimezone: evaluationSchedule.timeZone,
  } satisfies Partial<SignupLeadV4>;

  // Check if leadId already exists, update lead if it does
  // we don't need a new lead if we're resuming
  if (!leadId) {
    const leadResponse = await createSalesforceLead({
      ...leadObject,
      leadSource: 'Self-Referral - Web Inquiry',
    });
    leadId = leadResponse.id;
    // Store leadId in localStorage for persistence across form submissions
    setTempLeadId(leadId);
  } else {
    await updateSalesforceLead({
      ...leadObject,
      id: leadId,
    });
  }

  const {
    contactInfo: { relationshipToClient },
  } = data;

  const stripeData = await getStripeAccountIdByState(filters.stateCode);

  // Submit final form with schedule
  let scheduleResponse;

  const isCommercialInsurance = data.billingInfo.type === 'insurance-commercial';
  const isInsuranceOutOfNetwork = data.billingInfo.insuranceData.networkStatus === 'Out-of-network';
  const isMedicaidInsurance = data.billingInfo.type === 'insurance-medicaid';
  const isMedicareInsurance = data.billingInfo.type === 'insurance-medicare';

  const commercialEvalPrice = getEvaluationPrice({
    isPrivatePay: false,
    insurance: data.billingInfo.insuranceData,
    isOutOfNetwork: data.billingInfo.insuranceData.networkStatus === 'Out-of-network',
    chargeCommercialDepositsFeatureFlag: isCommercialInsurance ? flags?.chargeCommercialDeposits : undefined,
  });

  // TODO: move this function to a utils file
  const getCoPayAmount = () => {
    if (data.billingInfo.type === 'insurance-commercial') {
      if (isInsuranceOutOfNetwork) {
        return OUT_OF_NETWORK_EVALUATION_PRICE;
      }

      return (
        data.billingInfo.insuranceData.averageSessionPatientResponsibility ??
        DEFAULT_IN_NETWORK_ESTIMATES.defaultDeposit
      );
    }

    if (data.billingInfo.type === 'insurance-medicare') {
      // Medicare copay should be 0 dollars as per requirements
      return 0;
    }

    return undefined;
  };

  const schedules = getSchedulesPayload(evaluationSchedule);

  try {
    scheduleResponse = await scheduleEvaluationFromSignupV3({
      isResumeFlow: isResumedLead,
      address: { state: filters.stateCode },
      schedules,
      schedulesTimezone: evaluationSchedule.timeZone,
      clientFirstName: clientInfo.firstName,
      clientLastName: clientInfo.lastName,
      ...(data.loginContactInfo.isAlreadyContact
        ? {
            contactID: data.loginContactInfo.contactId,
          }
        : { password: data.loginContactInfo.newPassword }),

      contactFirstName:
        data.contactInfo.relationshipToClient === ContactRelationship.Client
          ? data.clientInfo.firstName
          : (data.contactInfo.firstName as string),
      contactLastName:
        data.contactInfo.relationshipToClient === ContactRelationship.Client
          ? data.clientInfo.lastName
          : (data.contactInfo.lastName as string),
      contactTimeZone: evaluationSchedule.timeZone,
      dob: extractDate(clientInfo.dateOfBirth),
      email: data.loginContactInfo.email,
      isClientMinor: filters.therapyTarget === 'child',
      phone: { mobile: removeWhitespaces(data.contactInfo.phone) },
      relationshipToClient: relationshipToClient,
      leadID: leadId as string,
      skipStripePayment: isCommercialInsurance || isMedicareInsurance ? false : true,
      isCommercialInsurance,
      isInsuranceOutOfNetwork,
      isMedicaidInsurance,
      isMedicareInsurance,
      amount: isCommercialInsurance || isMedicareInsurance ? commercialEvalPrice : undefined,
      ...(isCommercialInsurance && {
        chargeCommercialDeposits: flags?.chargeCommercialDeposits ?? false,
      }),
      coPay: getCoPayAmount(),
      entity: data.billingInfo.insuranceData.entity,
      primaryInsurance: {
        insuredFirstName: subscriberDetails.firstName,
        insuredLastName: subscriberDetails.lastName,
        insuredDob: new Date(subscriberDetails.dateOfBirth),
        insuredRelationshipToClient: SUBSCRIBER_RELATIONSHIP_CODE_MAP[subscriberDetails.relationShipToClient],
        insurancePayer: data.billingInfo.insuranceData.payerId,
        insurancePayerName: data.billingInfo.insuranceData.candidPayerName,
        insuredPayerType: getPayerTypeFromBillingInfo(data.billingInfo),
        insuredMemberID: data.billingInfo.memberId ?? '',
        insuredGroupID: data.billingInfo.groupId,
        ...getPrimaryPhysicianPayload(primaryPhysician as Physician),
      },
      stripeAccountID: isCommercialInsurance || isMedicareInsurance ? stripeData.accountId : undefined,
      stripePaymentMethodID:
        data.billingInfo.type === 'insurance-commercial' || data.billingInfo.type === 'insurance-medicare'
          ? data.billingInfo.stripeMethodId
          : undefined,
      insuranceAuthorizations: {
        evaluationAuthStatus: data.billingInfo.insuranceData.evalAuthorizationRequired ? 'awaiting' : undefined,
        sessionAuthStatus: data.billingInfo.insuranceData.visitAuthorizationRequired ? 'awaiting' : undefined,
      },

      prefilledAppointmentID: resumedLeadState?.prefilledAppointment?.appointmentID,
      clinicalAreas: data.concerns.clinicalAreas,
      clinicalConcerns: data.concerns.clinicalConcernKeys, // just as they are listed in the form
      filteringClinicalAreas: getFilteringAreasByConcernKeys(data.concerns.clinicalConcernKeys, filters.therapyTarget),
      mappedClinicalConcerns: mapClinicalConcernsToClinicalAreas(
        data.concerns.clinicalConcernKeys,
        filters.therapyTarget,
      ),
    });

    if (scheduleResponse.status === 'signup failed') {
      throw scheduleResponse;
    }
  } catch (e) {
    handleServerSideErrors({ data, filters, resumedLeadState, LDClient }, e);

    return {
      success: false,
      error: (e as ScheduleSignupEvaluationResponseError).error.code,
      message: (e as ScheduleSignupEvaluationResponseError).error.message,
    };
  }

  await trackSuccessfulEval(isResumedLead, params, LDClient);

  // Clear temporary leadId from localStorage on successful submission
  clearTempLeadId();

  onClearSignupLocalStorage();

  return {
    success: true,
    scheduleResponse,
  };
};

// @TODO(juan): extract this to a reusable function. For some reason it errors out if we do, need to investigate whats going on
const trackSuccessfulEval = async (isResumedLead: boolean = false, params: SubmitParams, LDClient?: LDClient) => {
  LDClient?.track(
    SIGNUP_LAUNCHDARKLY_EVENTS.LANDING_PAGE_EXPERIMENT.SUBMITTED_EVALUATION.SUCCESS,
    getLDTrackExtras(params),
  );

  try {
    await LDClient?.flush();
  } catch (e) {
    captureException(new SignupError('Failed to flush LaunchDarkly', { debugData: { e } }));
  }
  return new Promise(s => {
    mixpanel.track(
      DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.SUBMITTED,
      { isResumedLead },
      {
        send_immediately: true,
      },
      () => {
        s(true);
      },
    );
  });
};

const handleServerSideErrors = ({ data, filters, LDClient }: Omit<SubmitParams, 'flags'>, e: unknown) => {
  // @TODO refactor, proper error types, solve issue with axios error types
  const errorCode = get(e, 'originalError.response.data.error.code');

  // Ensure we're not losing the leadId when there's an error
  const leadId = data.leadId ?? getTempLeadId();

  if (errorCode !== 'card_declined') {
    LDClient?.track(SIGNUP_LAUNCHDARKLY_EVENTS.LANDING_PAGE_EXPERIMENT.SUBMITTED_EVALUATION.FAILURE);
    captureException(
      new SignupError('Error submitting Evaluation form', {
        debugData: {
          e,
          filters,
          data,
          leadId,
        },
      }),
    );
  }
};

const getSchedulesPayload = (evaluationSchedule: EvaluationScheduleFormType): SignupSchedule[] => {
  const schedules: SignupSchedule[] = [];
  if (evaluationSchedule.type === 'single') {
    schedules.push({
      duration: EvaluationDuration.Single,
      timeSlot: evaluationSchedule.timeSlot.time.toISOString(),
      calendarID: evaluationSchedule.timeSlot.calendarID.toString(),
    });
  } else if (isSplitEvaluationSchema(evaluationSchedule)) {
    schedules.push({
      duration: EvaluationDuration.Split,
      timeSlot: evaluationSchedule.firstTimeSlot.time.toISOString(),
      calendarID: evaluationSchedule.firstTimeSlot.calendarID.toString(),
    });

    if (evaluationSchedule.secondTimeSlot) {
      schedules.push({
        duration: EvaluationDuration.Split,
        timeSlot: evaluationSchedule.secondTimeSlot.time.toISOString(),
        calendarID: evaluationSchedule.secondTimeSlot.calendarID.toString(),
      });
    }
  }

  return schedules;
};

const getPrimaryPhysicianPayload = (primaryPhysician: Physician | undefined) => {
  if (!primaryPhysician) return {};

  return {
    primaryPhysicianName: primaryPhysician.name,
    primaryPhysicianOffice: primaryPhysician.office,
    primaryPhysicianPhone: primaryPhysician.phoneNumber,
    primaryPhysicianWebsite: primaryPhysician.website,
    primaryPhysicianNPI: primaryPhysician.npi,
  };
};
